// src/renderer/utils/project-files.ts

import type { Asset, FolderNode } from '../../core/types/asset.types';
import { useProjectPanelStore } from '../store/projectPanelStore';
import { getProjectRoot, readFile as fsReadFile } from '../services/FileSystemService';

/**
 * Helper to detect asset type from file extension
 */
const getAssetTypeFromExtension = (extension: string): Asset['type'] => {
  const ext = extension.toLowerCase();

  // Images / Sprites
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(ext)) {
    return 'sprite';
  }

  // Video
  if (['.mp4', '.webm', '.mov', '.avi', '.mkv'].includes(ext)) {
    return 'video';
  }

  // Audio - Music (longer formats typically used for music)
  if (['.mp3', '.ogg', '.flac', '.aac', '.m4a'].includes(ext)) {
    return 'audio-music';
  }

  // Audio - SFX (shorter formats typically used for sound effects)
  if (['.wav'].includes(ext)) {
    return 'audio-sfx';
  }

  // Scripts
  if (ext === '.js') {
    return 'script-js';
  }
  if (ext === '.ts' || ext === '.tsx') {
    return 'script-ts';
  }

  // Prefabs
  if (ext === '.prefab') {
    return 'prefab';
  }

  // Scenes
  if (ext === '.scene') {
    return 'scene';
  }

  // Fonts
  if (['.ttf', '.otf', '.woff', '.woff2'].includes(ext)) {
    return 'font';
  }

  // Spine animations
  if (ext === '.spine' || ext === '.atlas' || ext === '.skel') {
    return 'spine';
  }

  // JSON files (could be scenes, prefabs, or data)
  if (ext === '.json') {
    return 'unknown'; // We'll treat JSON as unknown unless it has a specific extension
  }

  return 'unknown';
};

/**
 * Generate a stable ID from a path
 */
const generateIdFromPath = (path: string): string => {
  // Create a simple hash from the path for stable IDs
  return path.replace(/[\/\\]/g, '_').replace(/[^a-zA-Z0-9_.-]/g, '') || 'root';
};

/**
 * Recursively read a directory and build folder/asset structures
 */
async function readDirectoryRecursive(
  handle: FileSystemDirectoryHandle,
  parentPath: string,
  parentId: string | null,
  folders: Record<string, FolderNode>,
  assets: Record<string, Asset>
): Promise<string[]> {
  const childIds: string[] = [];

  try {
    for await (const entry of handle.values()) {
      const entryPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
      const entryId = generateIdFromPath(entryPath);

      if (entry.kind === 'directory') {
        // Skip hidden directories and common non-asset directories
        if (entry.name.startsWith('.') || entry.name === 'node_modules') {
          continue;
        }

        const dirHandle = entry as FileSystemDirectoryHandle;

        // Create folder node
        const folderNode: FolderNode = {
          id: entryId,
          name: entry.name,
          path: entryPath,
          parentId: parentId,
          children: [],
          expanded: parentId === null, // Expand root-level folders by default
          type: 'folder',
        };

        folders[entryId] = folderNode;
        childIds.push(entryId);

        // Recursively read subdirectory
        const subChildren = await readDirectoryRecursive(
          dirHandle,
          entryPath,
          entryId,
          folders,
          assets
        );

        // Update folder children
        folders[entryId].children = subChildren;
      } else {
        // Skip hidden files
        if (entry.name.startsWith('.')) {
          continue;
        }

        const fileHandle = entry as FileSystemFileHandle;
        const lastDotIndex = entry.name.lastIndexOf('.');
        const extension = lastDotIndex !== -1 ? entry.name.slice(lastDotIndex) : '';
        const assetType = getAssetTypeFromExtension(extension);

        // Skip unknown file types (not recognized as assets) - but include common ones
        if (assetType === 'unknown') {
          continue;
        }

        // Get file metadata
        let fileSize = 0;
        let lastModified = new Date();
        try {
          const file = await fileHandle.getFile();
          fileSize = file.size;
          lastModified = new Date(file.lastModified);
        } catch {
          // If we can't read the file, use defaults
        }

        // Create asset node
        const asset: Asset = {
          id: entryId,
          name: entry.name,
          path: entryPath,
          type: assetType,
          extension,
          parentId: parentId,
          metadata: {
            fileSize,
          },
          createdAt: lastModified,
          modifiedAt: lastModified,
        };

        assets[entryId] = asset;
        childIds.push(entryId);
      }
    }
  } catch (err) {
    console.error(`Error reading directory ${parentPath}:`, err);
  }

  return childIds;
}

/**
 * Refresh the project files by reading the actual directory structure.
 * Uses FileSystemService.getProjectRoot() to get the current project handle.
 * This is the standalone function version that can be called outside of React hooks.
 */
export async function refreshProjectFiles(): Promise<void> {
  const handle = getProjectRoot();
  if (!handle) {
    console.warn('[project-files] No project root set, cannot refresh files');
    return;
  }

  const folders: Record<string, FolderNode> = {};
  const assets: Record<string, Asset> = {};
  const rootId = 'root';

  // Create root folder node
  const rootFolder: FolderNode = {
    id: rootId,
    name: handle.name,
    path: '',
    parentId: null,
    children: [],
    expanded: true,
    type: 'folder',
  };

  folders[rootId] = rootFolder;

  // Read the directory structure
  const rootChildren = await readDirectoryRecursive(
    handle,
    '',
    rootId,
    folders,
    assets
  );

  // Update root folder children
  folders[rootId].children = rootChildren;

  // Update the store with the real file tree
  const { setFileTree } = useProjectPanelStore.getState();
  setFileTree(folders, assets, rootId);

  console.log('[project-files] Loaded', Object.keys(folders).length, 'folders and', Object.keys(assets).length, 'assets');
}

/**
 * Refresh the project files by reading the actual directory structure from a handle.
 * @deprecated Use refreshProjectFiles() instead - it uses FileSystemService.getProjectRoot()
 */
export async function refreshProjectFilesFromHandle(
  handle: FileSystemDirectoryHandle
): Promise<void> {
  const folders: Record<string, FolderNode> = {};
  const assets: Record<string, Asset> = {};
  const rootId = 'root';

  // Create root folder node
  const rootFolder: FolderNode = {
    id: rootId,
    name: handle.name,
    path: '',
    parentId: null,
    children: [],
    expanded: true,
    type: 'folder',
  };

  folders[rootId] = rootFolder;

  // Read the directory structure
  const rootChildren = await readDirectoryRecursive(
    handle,
    '',
    rootId,
    folders,
    assets
  );

  // Update root folder children
  folders[rootId].children = rootChildren;

  // Update the store with the real file tree
  const { setFileTree } = useProjectPanelStore.getState();
  setFileTree(folders, assets, rootId);

  console.log('[project-files] Loaded', Object.keys(folders).length, 'folders and', Object.keys(assets).length, 'assets');
}

/**
 * Get file contents from the project using FileSystemService
 */
export async function readProjectFile(relativePath: string): Promise<string | null> {
  try {
    return await fsReadFile(relativePath);
  } catch (err) {
    console.error(`Error reading file ${relativePath}:`, err);
    return null;
  }
}
